/**
 * Date formatting utilities for Template Engine
 * All dates are displayed in IST (Asia/Kolkata, UTC+5:30)
 */

const IST_TIMEZONE = 'Asia/Kolkata'

/**
 * Convert a value into a Date, treating timezone-less ISO strings as UTC.
 *
 * The backend serializes timestamps with `datetime.utcnow().isoformat()`, which produces
 * strings like "2026-09-08T05:04:41" (no timezone suffix). JavaScript's `new Date()` would
 * otherwise interpret these as the browser's LOCAL time, which shifts the displayed IST time
 * incorrectly. We normalize by appending 'Z' when the string carries no timezone information
 * so that the value is always treated as UTC and then rendered in IST.
 *
 * @param {string|Date|number} value
 * @returns {Date}
 */
export const toUtcDate = (value) => {
  if (value instanceof Date) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    // ISO-like date/time without timezone info (no trailing Z and no +HH:MM / -HH:MM offset).
    const isoNoTz = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(trimmed)
    if (isoNoTz) {
      return new Date(trimmed.replace(' ', 'T') + 'Z')
    }
    return new Date(trimmed)
  }
  return new Date(value)
}

/**
 * Format a date string/Date to IST display format
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {object} options - Intl.DateTimeFormat options override
 * @returns {string} Formatted date in IST
 */
export const formatDateIST = (dateString, options = {}) => {
  if (!dateString) return ''
  try {
    const date = toUtcDate(dateString)
    return date.toLocaleString('en-IN', {
      timeZone: IST_TIMEZONE,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options
    })
  } catch (e) {
    return String(dateString)
  }
}

/**
 * Format date only (no time) in IST
 */
export const formatDateOnlyIST = (dateString, options = {}) => {
  if (!dateString) return ''
  try {
    const date = toUtcDate(dateString)
    return date.toLocaleString('en-IN', {
      timeZone: IST_TIMEZONE,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      ...options
    })
  } catch (e) {
    return String(dateString)
  }
}

/**
 * Format time only in IST
 */
export const formatTimeIST = (dateString, options = {}) => {
  if (!dateString) return ''
  try {
    const date = toUtcDate(dateString)
    return date.toLocaleString('en-IN', {
      timeZone: IST_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options
    })
  } catch (e) {
    return String(dateString)
  }
}

/**
 * Format relative time (e.g., "2 hours ago", "just now")
 */
export const formatRelativeIST = (dateString) => {
  if (!dateString) return ''
  try {
    const date = toUtcDate(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMin = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMin < 1) return 'Just now'
    if (diffMin < 60) return `${diffMin}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return formatDateOnlyIST(dateString)
  } catch (e) {
    return String(dateString)
  }
}
